/**
 * Ticket_Service.ts
 * Support tickets + handoff (delegates to SK Tickets / Chat via the adapter).
 */

import { apiClient } from '../../api/axiosInstance';
import { Hosp_Tickets_Url, Hosp_Ticket_Url, Hosp_Ticket_Handoff_Url } from '../../api/endpoints';
import { newIdempotencyKey } from '../../lib/idempotency';
import { getGuestsBatch } from '../crm/guest';
import type {
  CreateTicketInput, HandoffInput, TicketView, TicketDetailView,
} from '../../schemas/support/ticket';

/**
 * Resolve each ticket's guest name from sk-CRM by contactId (CRM is the SSOT for
 * names). Used by both bootstrap and the post-create reload so a ticket always
 * shows the real caller name, never the adapter's 'Guest' placeholder. One batch
 * call; rows without a contactId keep whatever `guest` the adapter sent.
 */
export const resolveTicketGuestNames = async (list: TicketView[]): Promise<TicketView[]> => {
  // Only CRM contact ids (ctc_) resolve — legacy caller ids (cid_) predate the
  // caller→CRM fix and aren't valid CRM ids. CRM's batch validator 400s the WHOLE
  // request if any id is malformed, so we must exclude cid_ (else no name resolves).
  const ids = list.map((t) => t.contactId).filter((x): x is string => !!x && x.startsWith('ctc_'));
  if (ids.length === 0) return list;
  const byId = await getGuestsBatch(ids).catch(() => ({} as Record<string, { name: string }>));
  return list.map((t) => (t.contactId && byId[t.contactId] ? { ...t, guest: byId[t.contactId].name } : t));
};

// List support tickets from the adapter (sk-tickets is the source of truth),
// with guest names resolved from CRM so the UI shows the real caller.
export const getTickets = async (): Promise<TicketView[]> => {
  const { data } = await apiClient.get<TicketView[]>(Hosp_Tickets_Url);
  return resolveTicketGuestNames(data);
};

// Full ticket detail + conversation thread.
export const getTicket = async (id: string): Promise<TicketDetailView> => {
  const { data } = await apiClient.get<TicketDetailView>(Hosp_Ticket_Url(id));
  return data;
};

export const createTicket = async (input: CreateTicketInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Tickets_Url, {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('ticket'),
  });
  return data;
};

export const handoffTicket = async (ticketId: string, input: HandoffInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Ticket_Handoff_Url(ticketId), {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('handoff'),
  });
  return data;
};
